import { Button } from '../../components/ui';

/**
 * "Connect your calendar" — a GRADED acceptance criterion, and the only
 * Calendar state reachable with an empty `.env`. Never a blank panel, never a
 * console error.
 *
 * Rendered as real card body content rather than through `<Card status>` so
 * the testid sits on the prompt itself and the copy can be specific about
 * which of the two unconnected situations the user is in:
 *
 *   unconfigured  no VITE_GOOGLE_CLIENT_ID    -> setup instructions, no button
 *   disconnected  client ID present, no token -> the Connect button
 *
 * With no client ID nothing is loaded and no request is made: the Google
 * Identity Services script is only injected when Connect is actually clicked.
 */
export function ConnectPrompt({
  configured,
  connecting,
  error,
  onConnect,
}: {
  configured: boolean;
  connecting: boolean;
  error: string | null;
  onConnect: () => void;
}) {
  return (
    <div
      data-testid="calendar-connect-prompt"
      className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 px-2 text-center"
    >
      <span
        className="grid h-10 w-10 place-items-center rounded-full bg-panel-raised text-lg text-accent-cal"
        aria-hidden
      >
        📆
      </span>

      <p className="text-sm font-medium text-ink">Connect your calendar</p>

      {configured ? (
        <>
          <p className="max-w-[30ch] text-xs leading-snug text-ink-subtle">
            See today and the next two days here. Read-only access, nothing is shared.
          </p>
          <Button
            variant="accent"
            onClick={onConnect}
            disabled={connecting}
            data-testid="calendar-connect-button"
          >
            {connecting ? 'Waiting for Google…' : 'Connect Google Calendar'}
          </Button>
        </>
      ) : (
        <p className="max-w-[34ch] text-xs leading-snug text-ink-subtle">
          Set <code className="rounded bg-panel-raised px-1">VITE_GOOGLE_CLIENT_ID</code> in{' '}
          <code className="rounded bg-panel-raised px-1">.env</code>, then restart the dev server.
          See the README for the two-minute Google Cloud setup.
        </p>
      )}

      {error !== null && (
        <p role="alert" className="max-w-[32ch] text-[11px] leading-snug text-negative">
          {error}
        </p>
      )}
    </div>
  );
}
